import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { Download, Edit, Filter, Plus, RefreshCw, Search, Trash2, Upload, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

// Custom table component with better responsive handling
const CustomTable = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <table
    ref={ref}
    className={cn('w-full caption-bottom text-sm', className)}
    {...props}
  />
));
CustomTable.displayName = 'CustomTable';

// Add line clamp utility styles
const lineClampStyles = `
  .line-clamp-1 {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-wrap: break-word;
  }
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-wrap: break-word;
  }
`;

export interface TableColumn<T> {
  key: keyof T | string;
  label: string;
  sortable?: boolean;
  render?: (value: any, item: T) => React.ReactNode;
  width?: string;
  hideOnMobile?: boolean; // New property for responsive hiding
  hideOnTablet?: boolean; // New property for tablet hiding
}

export interface FilterConfig {
  key: string;
  label: string;
  type: 'text' | 'select' | 'date';
  options?: { value: string; label: string }[];
  placeholder?: string;
}

export interface DeleteConfirmConfig<T> {
  title?: string | ((item: T) => string);
  description?: string | ((item: T) => string);
  confirmLabel?: string;
  cancelLabel?: string;
  getItemLabel?: (item: T) => string | undefined;
}

export interface BulkDeleteConfirmConfig {
  title?: string | ((count: number) => string);
  description?: string | ((count: number) => string);
  confirmLabel?: string;
  cancelLabel?: string;
}

export interface MasterDataTableProps<T> {
  title: string;
  description?: string;
  data: T[]; // Should always be an array, but we'll add runtime checks
  columns: TableColumn<T>[];
  isLoading?: boolean;
  onAdd: () => void;
  onEdit: (item: T) => void;
  onDelete: (item: T) => void;
  onRefresh?: () => void;
  onExport?: () => void;
  onImport?: (file: File) => void;
  onBulkDelete?: (items: T[]) => void;
  filters?: FilterConfig[];
  searchPlaceholder?: string;
  allowBulkOperations?: boolean;
  allowImportExport?: boolean;
  itemIdKey?: keyof T;
  // Server-side filtering and pagination props
  serverSide?: boolean;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSearchChange?: (search: string) => void;
  onFiltersChange?: (filters: Record<string, string>) => void;
  onSortChange?: (sort: { sortBy?: string; sortOrder?: 'asc' | 'desc' }) => void;
  currentSearch?: string;
  currentFilters?: Record<string, string>;
  currentSort?: { sortBy?: string; sortOrder?: 'asc' | 'desc' };
  deleteConfirm?: boolean | DeleteConfirmConfig<T>;
  bulkDeleteConfirm?: boolean | BulkDeleteConfirmConfig;
}

export function MasterDataTable<T extends Record<string, any>>({
  title,
  description,
  data = [], // Provide default empty array
  columns,
  isLoading = false,
  onAdd,
  onEdit,
  onDelete,
  onRefresh,
  onExport,
  onImport,
  onBulkDelete,
  filters = [],
  searchPlaceholder = "Search...",
  allowBulkOperations = false,
  allowImportExport = false,
  itemIdKey = '_id' as keyof T,
  // Server-side props
  serverSide = false,
  pagination,
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  onFiltersChange,
  onSortChange,
  currentSearch = '',
  currentFilters = {},
  currentSort = {},
  deleteConfirm,
  bulkDeleteConfirm,
}: MasterDataTableProps<T>) {
  // Use server-side state if available, otherwise local state
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [localFilterValues, setLocalFilterValues] = useState<Record<string, string>>({});

  // Server-side search UX: keep input responsive, debounce requests to avoid firing on every keypress.
  const [searchInput, setSearchInput] = useState(currentSearch || '');
  const [isSearchDebouncing, setIsSearchDebouncing] = useState(false);
  const searchDebounceTimer = useRef<number | null>(null);
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!serverSide) return;
    // Sync input when parent state changes (e.g., Clear All)
    setSearchInput(currentSearch || '');
  }, [serverSide, currentSearch]);

  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        window.clearTimeout(searchDebounceTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key !== '/') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }

      event.preventDefault();
      searchInputRef.current?.focus();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [localSortConfig, setLocalSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [pageJump, setPageJump] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<
    | { type: 'single'; item: T }
    | { type: 'bulk'; items: T[] }
    | null
  >(null);

  const deleteConfirmConfig = React.useMemo(() => {
    if (!deleteConfirm) return null;
    return typeof deleteConfirm === 'boolean' ? {} : deleteConfirm;
  }, [deleteConfirm]);

  const bulkDeleteConfirmConfig = React.useMemo(() => {
    const resolved = bulkDeleteConfirm === undefined ? deleteConfirm : bulkDeleteConfirm;
    if (!resolved) return null;
    return typeof resolved === 'boolean' ? {} : resolved;
  }, [bulkDeleteConfirm, deleteConfirm]);

  const inferItemLabel = React.useCallback(
    (item: T) => {
      const candidateKeys = [
        'name',
        'title',
        'code',
        'awbNumber',
        'manifestNumber',
        'bagNo',
        'pincode',
        'email',
        'phone',
        'number',
        'srNo',
      ];
      for (const key of candidateKeys) {
        const value = item[key];
        if (value != null && String(value).trim()) {
          return String(value);
        }
      }
      const fallbackId = item[itemIdKey];
      return fallbackId != null ? String(fallbackId) : undefined;
    },
    [itemIdKey],
  );

  useEffect(() => {
    if (!serverSide || !pagination) return;
    setPageJump(String(pagination.page));
  }, [serverSide, pagination?.page]);

  // Determine which state to use
  const searchTerm = serverSide ? searchInput : localSearchTerm;
  const filterValues = serverSide ? currentFilters : localFilterValues;
  const sortConfig = serverSide ? 
    (currentSort?.sortBy ? { key: currentSort.sortBy, direction: currentSort.sortOrder || 'asc' } : null) :
    localSortConfig;
  const deferredSearchTerm = React.useDeferredValue(searchTerm);

  const resetToFirstPage = React.useCallback(() => {
    if (!serverSide || !onPageChange) return;
    if (pagination?.page === 1) return;
    onPageChange(1);
  }, [onPageChange, pagination?.page, serverSide]);

  // Filter and search data with improved logic (only for client-side mode)
  const filteredData = React.useMemo(() => {
    // Ensure data is always an array
    if (!data || !Array.isArray(data)) {
      return [];
    }
    
    // If server-side, return data as-is since filtering is done on server
    if (serverSide) {
      return data;
    }
    
    let filtered = data;
    const activeSearchTerm = serverSide ? searchTerm : deferredSearchTerm;

    // Apply search - search across multiple fields intelligently
    if (activeSearchTerm && activeSearchTerm.trim()) {
      const searchLower = activeSearchTerm.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        // Search in common fields that are likely to be meaningful
        const searchableFields = [
          'name', 'code', 'title', 'description', 'email', 'phone', 'address',
          'currencyName', 'currencySymbol', 'countryCode', 'dialCode',
          'type', 'category', 'status'
        ];
        
        // First try searching in specific searchable fields
        const fieldMatch = searchableFields.some(field => {
          const fieldValue = item[field];
          if (fieldValue != null) {
            const stringValue = String(fieldValue).toLowerCase();
            return stringValue.includes(searchLower);
          }
          return false;
        });
        
        if (fieldMatch) return true;
        
        // If no field match, fall back to searching all values (excluding internal fields)
        return Object.entries(item).some(([key, value]) => {
          // Skip internal fields like _id, timestamps, etc.
          if (key.startsWith('_') || key.includes('At') || key.includes('By')) {
            return false;
          }
          
          if (value != null) {
            const stringValue = String(value).toLowerCase();
            return stringValue.includes(searchLower);
          }
          return false;
        });
      });
    }

    // Apply filters with improved logic
    Object.entries(filterValues).forEach(([key, value]) => {
      if (value && value !== '' && value !== 'all') {
        filtered = filtered.filter((item) => {
          const itemValue = item[key];
          
          // Handle different data types appropriately
          if (typeof itemValue === 'boolean') {
            // For boolean values, compare directly
            const filterBoolean = String(value) === 'true';
            return itemValue === filterBoolean;
          } else if (typeof itemValue === 'number') {
            // For numbers, try exact match or range
            return String(itemValue) === String(value);
          } else if (itemValue && typeof itemValue === 'string') {
            // For status field, use exact match; for other fields, use includes
            if (key === 'status') {
              return itemValue.toLowerCase() === String(value).toLowerCase();
            } else {
              return itemValue.toLowerCase().includes(String(value).toLowerCase());
            }
          } else {
            // For other types, convert to string and compare
            const stringValue = String(itemValue).toLowerCase();
            const filterValue = String(value).toLowerCase();
            
            // For status field, use exact match; for other fields, use includes
            if (key === 'status') {
              return stringValue === filterValue;
            } else {
              return stringValue.includes(filterValue);
            }
          }
        });
      }
    });

    // Apply sorting with improved logic
    if (sortConfig) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
        if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
        
        // Handle dates
        if (sortConfig.key.includes('At') || sortConfig.key.includes('Date')) {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        
        // Handle strings (case-insensitive)
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [data, deferredSearchTerm, filterValues, searchTerm, sortConfig, serverSide]);

  // Keep bulk selection in sync with current results (filters/search/pagination).
  // This prevents “hidden” selected rows after a filter/search change.
  useEffect(() => {
    if (!allowBulkOperations) return;

    const visibleIds = new Set(filteredData.map((item) => String(item[itemIdKey])));

    setSelectedItems((prev) => {
      if (prev.size === 0) return prev;

      const next = new Set<string>();
      prev.forEach((id) => {
        if (visibleIds.has(id)) next.add(id);
      });

      return next.size === prev.size ? prev : next;
    });
  }, [allowBulkOperations, filteredData, itemIdKey]);

  const handleSort = (key: string) => {
    if (serverSide && onSortChange) {
      const newDirection = (currentSort?.sortBy === key && currentSort?.sortOrder === 'asc') ? 'desc' : 'asc';
      resetToFirstPage();
      onSortChange({ sortBy: key, sortOrder: newDirection });
    } else {
      setLocalSortConfig((current) => {
        if (current?.key === key) {
          return {
            key,
            direction: current.direction === 'asc' ? 'desc' : 'asc',
          };
        }
        return { key, direction: 'asc' };
      });
    }
  };

  const handleSortKeyDown = (
    event: React.KeyboardEvent<HTMLTableCellElement>,
    key: string,
    sortable?: boolean
  ) => {
    if (!sortable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSort(key);
    }
  };

  const handleSearchChange = (value: string) => {
    if (serverSide) {
      setSearchInput(value);

      if (searchDebounceTimer.current) {
        window.clearTimeout(searchDebounceTimer.current);
      }

      // Debounce server calls so typing doesn't spam requests.
      setIsSearchDebouncing(true);
      searchDebounceTimer.current = window.setTimeout(() => {
        resetToFirstPage();
        onSearchChange?.(value);
        setIsSearchDebouncing(false);
      }, 350);
    } else {
      setLocalSearchTerm(value);
    }
  };

  const commitServerSearch = (value: string) => {
    if (!serverSide) return;
    if (searchDebounceTimer.current) {
      window.clearTimeout(searchDebounceTimer.current);
      searchDebounceTimer.current = null;
    }
    setIsSearchDebouncing(false);
    resetToFirstPage();
    onSearchChange?.(value);
  };

  const commitPageJump = (value: string) => {
    if (!pagination) return;
    const trimmed = value.trim();
    if (!trimmed) {
      setPageJump(String(pagination.page));
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
      setPageJump(String(pagination.page));
      return;
    }
    const clamped = Math.min(Math.max(1, Math.floor(parsed)), pagination.totalPages);
    setPageJump(String(clamped));
    if (clamped !== pagination.page) {
      onPageChange?.(clamped);
    }
  };

  const handlePageJumpKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!pagination) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      commitPageJump(pageJump);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setPageJump(String(pagination.page));
    }
  };

  const handleSearchClear = () => {
    if (searchDebounceTimer.current) {
      window.clearTimeout(searchDebounceTimer.current);
    }
    setIsSearchDebouncing(false);

    if (serverSide) {
      setSearchInput('');
      resetToFirstPage();
      onSearchChange?.('');
    } else {
      setLocalSearchTerm('');
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filterValues, [key]: value };
    if (serverSide && onFiltersChange) {
      resetToFirstPage();
      onFiltersChange(newFilters);
    } else {
      setLocalFilterValues(newFilters);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = filteredData.map((item) => String(item[itemIdKey]));
      setSelectedItems(new Set(allIds));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (itemId: string, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(itemId);
    } else {
      newSelected.delete(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleBulkDelete = () => {
    if (selectedItems.size > 0 && onBulkDelete) {
      const itemsToDelete = filteredData.filter((item) =>
        selectedItems.has(String(item[itemIdKey]))
      );
      if (!itemsToDelete.length) return;
      if (bulkDeleteConfirmConfig) {
        setDeleteDialog({ type: 'bulk', items: itemsToDelete });
        return;
      }
      onBulkDelete(itemsToDelete);
      setSelectedItems(new Set());
    }
  };

  const handleImportFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onImport) {
      onImport(file);
    }
    // Reset input
    event.target.value = '';
  };

  const handleImportClick = () => {
    importInputRef.current?.click();
  };

  const clearFilters = () => {
    // Ensure we don't leave a debounce timer hanging when the user clears everything.
    if (searchDebounceTimer.current) {
      window.clearTimeout(searchDebounceTimer.current);
      searchDebounceTimer.current = null;
    }
    setIsSearchDebouncing(false);

    if (serverSide) {
      // Keep the input responsive even if parent state updates async.
      setSearchInput('');
      resetToFirstPage();
      onSearchChange?.('');
      onFiltersChange?.({});
      onSortChange?.({});
    } else {
      setLocalSearchTerm('');
      setLocalFilterValues({});
      setLocalSortConfig(null);
    }

    // Clearing filters should also clear any bulk selection to avoid accidental deletes.
    setSelectedItems(new Set());
  };

  const activeFilterCount =
    (searchTerm && String(searchTerm).trim() ? 1 : 0) +
    Object.values(filterValues).filter((value) => value && value !== '' && value !== 'all').length +
    (sortConfig ? 1 : 0);

  const hasActiveFilters = activeFilterCount > 0;

  const mobileVisibleColumns = React.useMemo(
    () => columns.filter((column) => !column.hideOnMobile),
    [columns]
  );
  const mobilePrimaryColumns = React.useMemo(
    () => mobileVisibleColumns.slice(0, 3),
    [mobileVisibleColumns]
  );
  const mobileSecondaryColumns = React.useMemo(
    () => mobileVisibleColumns.slice(3),
    [mobileVisibleColumns]
  );

  const filterConfigMap = React.useMemo(() => {
    const map = new Map<string, FilterConfig>();
    filters.forEach((filter) => {
      map.set(filter.key, filter);
    });
    return map;
  }, [filters]);

  const clearSort = () => {
    if (serverSide && onSortChange) {
      onSortChange({});
      return;
    }
    setLocalSortConfig(null);
  };

  const clearSingleFilter = (key: string) => {
    const config = filterConfigMap.get(key);
    if (config?.type === 'select') {
      handleFilterChange(key, 'all');
      return;
    }
    handleFilterChange(key, '');
  };

  const getFilterDisplayValue = (config: FilterConfig | undefined, value: string) => {
    if (!config) return String(value);
    if (config.type === 'select') {
      const match = config.options?.find((option) => String(option.value) === String(value));
      return match?.label ?? String(value);
    }
    if (value === 'true') return 'Yes';
    if (value === 'false') return 'No';
    return String(value);
  };

  const handleDeleteClick = (item: T) => {
    if (deleteConfirmConfig) {
      setDeleteDialog({ type: 'single', item });
      return;
    }
    onDelete(item);
  };

  const deleteDialogTitle = React.useMemo(() => {
    if (!deleteDialog) return '';
    if (deleteDialog.type === 'single') {
      if (!deleteConfirmConfig) return '';
      if (typeof deleteConfirmConfig.title === 'function') {
        return deleteConfirmConfig.title(deleteDialog.item);
      }
      if (deleteConfirmConfig.title) return deleteConfirmConfig.title;
      const label =
        deleteConfirmConfig.getItemLabel?.(deleteDialog.item) ?? inferItemLabel(deleteDialog.item);
      return label ? `Delete ${label}?` : 'Delete record?';
    }
    if (deleteDialog.type === 'bulk') {
      if (!bulkDeleteConfirmConfig) return '';
      if (typeof bulkDeleteConfirmConfig.title === 'function') {
        return bulkDeleteConfirmConfig.title(deleteDialog.items.length);
      }
      if (bulkDeleteConfirmConfig.title) return bulkDeleteConfirmConfig.title;
      return `Delete ${deleteDialog.items.length} records?`;
    }
    return '';
  }, [deleteDialog, deleteConfirmConfig, bulkDeleteConfirmConfig, inferItemLabel]);

  const deleteDialogDescription = React.useMemo(() => {
    if (!deleteDialog) return '';
    if (deleteDialog.type === 'single') {
      if (!deleteConfirmConfig) return '';
      if (typeof deleteConfirmConfig.description === 'function') {
        return deleteConfirmConfig.description(deleteDialog.item);
      }
      if (deleteConfirmConfig.description) return deleteConfirmConfig.description;
      return 'This action cannot be undone.';
    }
    if (deleteDialog.type === 'bulk') {
      if (!bulkDeleteConfirmConfig) return '';
      if (typeof bulkDeleteConfirmConfig.description === 'function') {
        return bulkDeleteConfirmConfig.description(deleteDialog.items.length);
      }
      if (bulkDeleteConfirmConfig.description) return bulkDeleteConfirmConfig.description;
      return 'This action cannot be undone.';
    }
    return '';
  }, [deleteDialog, deleteConfirmConfig, bulkDeleteConfirmConfig]);

  const deleteDialogConfirmLabel = React.useMemo(() => {
    if (!deleteDialog) return 'Delete';
    if (deleteDialog.type === 'bulk') {
      return bulkDeleteConfirmConfig?.confirmLabel ?? 'Delete Selected';
    }
    return deleteConfirmConfig?.confirmLabel ?? 'Delete';
  }, [deleteDialog, deleteConfirmConfig, bulkDeleteConfirmConfig]);

  const deleteDialogCancelLabel = React.useMemo(() => {
    if (!deleteDialog) return 'Cancel';
    if (deleteDialog.type === 'bulk') {
      return bulkDeleteConfirmConfig?.cancelLabel ?? 'Cancel';
    }
    return deleteConfirmConfig?.cancelLabel ?? 'Cancel';
  }, [deleteDialog, deleteConfirmConfig, bulkDeleteConfirmConfig]);

  const handleConfirmDelete = () => {
    if (!deleteDialog) return;
    if (deleteDialog.type === 'single') {
      onDelete(deleteDialog.item);
    } else if (deleteDialog.type === 'bulk') {
      onBulkDelete?.(deleteDialog.items);
      setSelectedItems(new Set());
    }
    setDeleteDialog(null);
  };

  const activeFilterChips = [
    ...(String(searchTerm || '').trim()
      ? [
          {
            id: 'search',
            label: 'Search',
            value: String(searchTerm).trim(),
            onClear: handleSearchClear,
          },
        ]
      : []),
    ...Object.entries(filterValues)
      .filter(([_, value]) => value && value !== '' && value !== 'all')
      .map(([key, value]) => {
        const config = filterConfigMap.get(key);
        return {
          id: `filter-${key}`,
          label: config?.label ?? key,
          value: getFilterDisplayValue(config, String(value)),
          onClear: () => clearSingleFilter(key),
        };
      }),
    ...(sortConfig
      ? [
          {
            id: 'sort',
            label: 'Sort',
            value: `${filterConfigMap.get(sortConfig.key)?.label ?? sortConfig.key} (${sortConfig.direction})`,
            onClear: clearSort,
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-6">
      {/* Inject custom styles */}
      <style>{lineClampStyles}</style>
      
      {/* Header - Improved Mobile Layout */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold truncate">{title}</h1>
          {description && (
            <p className="text-sm sm:text-base text-muted-foreground mt-1">{description}</p>
          )}
        </div>

        {onImport && allowImportExport && (
          <input
            ref={importInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleImportFile}
            className="hidden"
          />
        )}
        
        {/* Desktop Actions */}
        <div className="hidden sm:flex items-center space-x-2 flex-shrink-0">
          {onRefresh && (
            <Button
              variant="outline"
              size="icon"
              onClick={onRefresh}
              aria-label="Refresh list"
              title="Refresh list"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
          {allowImportExport && (
            <>
              {onExport && (
                <Button variant="outline" onClick={onExport} aria-label="Export data" title="Export data">
                  <Download className="mr-2 h-4 w-4" />
                  <span className="hidden lg:inline">Export</span>
                </Button>
              )}
              {onImport && (
                <Button variant="outline" onClick={handleImportClick} aria-label="Import data" title="Import data">
                  <Upload className="mr-2 h-4 w-4" />
                  <span className="hidden lg:inline">Import</span>
                </Button>
              )}
            </>
          )}
          <Button onClick={onAdd}>
            <Plus className="mr-2 h-4 w-4" />
            <span className="hidden md:inline">Add New</span>
            <span className="md:hidden">Add</span>
          </Button>
        </div>

        {/* Mobile Actions */}
        <div className="flex sm:hidden items-center justify-between space-x-2">
          <div className="flex items-center space-x-2">
            {onRefresh && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRefresh}
                aria-label="Refresh list"
                title="Refresh list"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            )}
            {allowImportExport && (
              <>
                {onExport && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onExport}
                    aria-label="Export data"
                    title="Export data"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                )}
                {onImport && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleImportClick}
                    aria-label="Import data"
                    title="Import data"
                  >
                    <Upload className="h-4 w-4" />
                  </Button>
                )}
              </>
            )}
          </div>
          <Button onClick={onAdd} size="sm">
            <Plus className="mr-2 h-4 w-4" />
            Add
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <div className="space-y-1">
              <CardTitle className="text-lg">Search & Filters</CardTitle>
              {hasActiveFilters && (
                <p className="text-sm text-muted-foreground">
                  {serverSide
                    ? `Showing ${filteredData.length} of ${pagination?.total ?? data.length} records`
                    : `${filteredData.length} of ${data.length} records match your criteria`}
                </p>
              )}
            </div>
            <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-x-2 sm:space-y-0">
              {filters.length > 0 && (
                <Button
                  variant={showFilters ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="w-full sm:w-auto"
                >
                  <Filter className="mr-2 h-4 w-4" />
                  <span className="flex-1 text-left sm:text-center">
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                  </span>
                  {hasActiveFilters && !showFilters && (
                    <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-primary rounded-full">
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              )}
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters} className="w-full sm:w-auto">
                  Clear All
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                ref={searchInputRef}
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape' && searchTerm) {
                    event.preventDefault();
                    handleSearchClear();
                    return;
                  }
                  if (event.key === 'Enter' && serverSide) {
                    event.preventDefault();
                    const nextValue = (event.target as HTMLInputElement).value;
                    setSearchInput(nextValue);
                    commitServerSearch(nextValue);
                  }
                }}
                className={cn('pl-10 h-12 text-sm', serverSide ? 'pr-16' : 'pr-10')}
                aria-keyshortcuts="/"
                title="Press / to focus search"
              />

              <div className="absolute right-3 top-3 flex items-center gap-2">
                {serverSide && isSearchDebouncing && (
                  <LoadingSpinner size="sm" className="text-muted-foreground" />
                )}
                {searchTerm && (
                  <button
                    onClick={handleSearchClear}
                    className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
                    type="button"
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {hasActiveFilters && activeFilterChips.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Active filters
                </span>
                {activeFilterChips.map((chip) => (
                  <Badge
                    key={chip.id}
                    variant="secondary"
                    className="gap-1 pr-1 text-xs font-medium"
                  >
                    <span className="max-w-[220px] truncate" title={`${chip.label}: ${chip.value}`}>
                      {chip.label}: {chip.value}
                    </span>
                    <button
                      type="button"
                      onClick={chip.onClear}
                      className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/70"
                      aria-label={`Clear ${chip.label}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}

            {/* Filters - Improved Mobile Layout */}
            {showFilters && filters.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filters.map((filter) => (
                  <div key={filter.key} className="space-y-2">
                    <Label className="text-sm font-medium">{filter.label}</Label>
                    {filter.type === 'select' ? (
                      <Select
                        value={filterValues[filter.key] ? String(filterValues[filter.key]) : 'all'}
                        onValueChange={(value) => handleFilterChange(filter.key, value)}
                      >
                        <SelectTrigger className="h-10">
                          <SelectValue placeholder={filter.placeholder || `Select ${filter.label}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {filter.options?.filter((option) => option.value != null && option.value !== '').map((option) => (
                            <SelectItem key={String(option.value)} value={String(option.value)}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        type={filter.type}
                        placeholder={filter.placeholder}
                        value={filterValues[filter.key] || ''}
                        onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                        className="h-10"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {allowBulkOperations && selectedItems.size > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">
                  {selectedItems.size} item(s) selected
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedItems(new Set())}
                >
                  Clear Selection
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Selected
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{title} ({serverSide && pagination ? pagination.total : filteredData.length})</span>
            <div className="text-sm font-normal text-muted-foreground">
              {serverSide && pagination 
                ? `${pagination.total} total records`
                : filteredData.length === 0 ? 'No records' : filteredData.length === 1 ? '1 record' : `${filteredData.length} records`}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <>
              {/* Desktop skeleton */}
              <div className="hidden md:block">
                <div className="overflow-x-auto">
                  <CustomTable className="border-collapse">
                    <TableHeader>
                      <TableRow className="border-b">
                        {allowBulkOperations && (
                          <TableHead className="w-12 text-center p-4">
                            <div className="h-4 w-4 rounded bg-muted animate-pulse mx-auto" />
                          </TableHead>
                        )}
                        {columns.map((column, index) => (
                          <TableHead
                            key={String(column.key)}
                            className={cn(
                              "text-left font-semibold text-muted-foreground p-4 border-b",
                              column.hideOnTablet && "hidden lg:table-cell"
                            )}
                          >
                            <div className="h-4 rounded bg-muted animate-pulse" style={{ width: index % 3 === 0 ? 120 : index % 3 === 1 ? 160 : 90 }} />
                          </TableHead>
                        ))}
                        <TableHead className="w-24 text-center p-4 border-b">
                          <div className="h-4 w-16 rounded bg-muted animate-pulse mx-auto" />
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Array.from({ length: Math.min(pagination?.limit || 10, 8) }).map((_, rowIdx) => (
                        <TableRow key={`skeleton-row-${rowIdx}`} className="border-b">
                          {allowBulkOperations && (
                            <TableCell className="text-center p-4">
                              <div className="h-4 w-4 rounded bg-muted animate-pulse mx-auto" />
                            </TableCell>
                          )}
                          {columns.map((column, colIdx) => (
                            <TableCell
                              key={String(column.key)}
                              className={cn(
                                "p-4 align-top",
                                column.hideOnTablet && "hidden lg:table-cell"
                              )}
                            >
                              <div
                                className="h-4 rounded bg-muted animate-pulse"
                                style={{ width: colIdx % 4 === 0 ? '70%' : colIdx % 4 === 1 ? '45%' : colIdx % 4 === 2 ? '85%' : '55%' }}
                              />
                            </TableCell>
                          ))}
                          <TableCell className="text-center p-4">
                            <div className="flex items-center justify-center space-x-2">
                              <div className="h-8 w-8 rounded bg-muted animate-pulse" />
                              <div className="h-8 w-8 rounded bg-muted animate-pulse" />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </CustomTable>
                </div>
              </div>

              {/* Mobile skeleton */}
              <div className="block md:hidden">
                <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Card key={`skeleton-card-${i}`} className="shadow-sm">
                      <CardContent className="p-3 sm:p-4">
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <div className="h-4 w-2/3 rounded bg-muted animate-pulse" />
                            <div className="h-4 w-1/2 rounded bg-muted animate-pulse" />
                            <div className="h-4 w-3/4 rounded bg-muted animate-pulse" />
                          </div>
                          <div className="pt-3 border-t border-border flex space-x-2">
                            <div className="h-9 flex-1 rounded bg-muted animate-pulse" />
                            <div className="h-9 flex-1 rounded bg-muted animate-pulse" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block">
                <div className="overflow-x-auto">
                  <CustomTable className="border-collapse">
                    <TableHeader>
                      <TableRow className="border-b">
                        {allowBulkOperations && (
                          <TableHead className="w-12 text-center p-4">
                            <input
                              type="checkbox"
                              checked={
                                filteredData.length > 0 &&
                                filteredData.every((item) =>
                                  selectedItems.has(String(item[itemIdKey]))
                                )
                              }
                              onChange={(e) => handleSelectAll(e.target.checked)}
                              className="rounded border-input"
                              aria-label="Select all rows"
                            />
                          </TableHead>
                        )}
                        {columns.map((column) => {
                          const isSorted = sortConfig?.key === column.key;
                          const sortDirection = isSorted
                            ? (sortConfig?.direction === 'asc' ? 'ascending' : 'descending')
                            : undefined;

                          return (
                            <TableHead
                              key={String(column.key)}
                              scope="col"
                              aria-sort={column.sortable ? (sortDirection ?? 'none') : undefined}
                              tabIndex={column.sortable ? 0 : undefined}
                              className={cn(
                                "text-left font-semibold text-muted-foreground p-4 border-b",
                                column.hideOnTablet && "hidden lg:table-cell",
                                column.sortable && "cursor-pointer hover:bg-muted/50 select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1 focus-visible:ring-offset-background"
                              )}
                              onClick={() => column.sortable && handleSort(String(column.key))}
                              onKeyDown={(event) => handleSortKeyDown(event, String(column.key), column.sortable)}
                            >
                              <div className="flex items-center space-x-2">
                                <span>{column.label}</span>
                                {column.sortable && isSorted && (
                                  <span className="text-xs">
                                    {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                              </div>
                            </TableHead>
                          );
                        })}
                        <TableHead className="w-24 text-center p-4 border-b">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredData.map((item) => (
                        <TableRow key={String(item[itemIdKey])} className="hover:bg-muted/30 border-b">
                          {allowBulkOperations && (
                            <TableCell className="text-center p-4">
                              <input
                                type="checkbox"
                                checked={selectedItems.has(String(item[itemIdKey]))}
                                onChange={(e) =>
                                  handleSelectItem(String(item[itemIdKey]), e.target.checked)
                                }
                                className="rounded border-input"
                                aria-label={
                                  inferItemLabel(item)
                                    ? `Select ${inferItemLabel(item)}`
                                    : 'Select item'
                                }
                              />
                            </TableCell>
                          )}
                          {columns.map((column, columnIndex) => (
                            <TableCell 
                              key={String(column.key)}
                              className={cn(
                                "p-4 align-top",
                                column.hideOnTablet && "hidden lg:table-cell",
                                columnIndex === 0 && "font-medium"
                              )}
                            >
                              <div className="max-w-xs overflow-hidden">
                                {column.render
                                  ? column.render(item[column.key as keyof T], item)
                                  : (
                                    <span className="block truncate" title={String(item[column.key as keyof T] || '-')}>
                                      {String(item[column.key as keyof T] || '-')}
                                    </span>
                                  )}
                              </div>
                            </TableCell>
                          ))}
                          <TableCell className="text-center p-4">
                            <div className="flex items-center justify-center space-x-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onEdit(item)}
                                className="h-8 w-8 p-0"
                              >
                                <Edit className="h-4 w-4" />
                                <span className="sr-only">Edit</span>
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteClick(item)}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                              >
                                <Trash2 className="h-4 w-4" />
                                <span className="sr-only">Delete</span>
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredData.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={columns.length + (allowBulkOperations ? 2 : 1)}
                            className="text-center text-muted-foreground h-40 p-8"
                          >
                            <div className="flex flex-col items-center justify-center space-y-3">
                              <div className="text-4xl opacity-50">📋</div>
                              <div className="text-lg font-medium">
                                {hasActiveFilters ? 'No results found' : 'No records yet'}
                              </div>
                              <div className="text-sm">
                                {hasActiveFilters
                                  ? 'Try adjusting your search/filters, or clear them to see everything.'
                                  : 'Create your first record to get started.'}
                              </div>
                              <div className="pt-2 flex items-center gap-2">
                                {hasActiveFilters ? (
                                  <Button variant="outline" size="sm" onClick={clearFilters}>
                                    Clear filters
                                  </Button>
                                ) : (
                                  <Button size="sm" onClick={onAdd}>
                                    <Plus className="mr-2 h-4 w-4" />
                                    Add New
                                  </Button>
                                )}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </CustomTable>
                </div>
              </div>
              
              {/* Mobile/Tablet Card View - Enhanced Responsive Design */}
              <div className="block md:hidden">
                {filteredData.length > 0 ? (
                  <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
                    {filteredData.map((item) => (
                      <Card key={String(item[itemIdKey])} className="shadow-sm hover:shadow-md transition-all duration-200 hover:scale-[1.01]">
                        <CardContent className="p-3 sm:p-4">
                          <div className="space-y-3">
                            {/* Main content - Show primary columns first */}
                            <div className="space-y-2">
                              {mobilePrimaryColumns.map((column) => (
                                <div key={String(column.key)} className="flex justify-between items-start gap-3">
                                  <span className="text-sm font-medium text-muted-foreground flex-shrink-0 min-w-0 max-w-[40%]">
                                    {column.label}:
                                  </span>
                                  <span
                                    className="text-sm text-right min-w-0 flex-1 break-words font-medium"
                                    title={
                                      column.render
                                        ? undefined
                                        : String(item[column.key as keyof T] || '-')
                                    }
                                  >
                                    {column.render
                                      ? column.render(item[column.key as keyof T], item)
                                      : String(item[column.key as keyof T] || '-')}
                                  </span>
                                </div>
                              ))}
                            </div>
                            
                            {/* Secondary information - Collapsible for smaller screens */}
                            {mobileSecondaryColumns.length > 0 && (
                              <div className="pt-2 border-t border-border">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                                  {mobileSecondaryColumns.map((column) => (
                                    <div key={String(column.key)} className="flex flex-col space-y-1">
                                      <span className="text-muted-foreground font-medium">
                                        {column.label}
                                      </span>
                                      <span
                                        className="text-foreground truncate"
                                        title={
                                          column.render
                                            ? undefined
                                            : String(item[column.key as keyof T] || '-')
                                        }
                                      >
                                        {column.render
                                          ? column.render(item[column.key as keyof T], item)
                                          : String(item[column.key as keyof T] || '-')}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Actions - Improved mobile layout */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 pt-3 border-t border-border">
                              {allowBulkOperations && (
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={selectedItems.has(String(item[itemIdKey]))}
                                    onChange={(e) =>
                                      handleSelectItem(String(item[itemIdKey]), e.target.checked)
                                    }
                                    className="rounded border-input mr-2 h-4 w-4"
                                    aria-label={
                                      inferItemLabel(item)
                                        ? `Select ${inferItemLabel(item)}`
                                        : 'Select item'
                                    }
                                  />
                                  <span className="text-sm text-muted-foreground">Select item</span>
                                </div>
                              )}
                              <div className="flex space-x-2 sm:ml-auto">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => onEdit(item)}
                                  className="flex-1 sm:flex-none h-9 text-xs sm:text-sm"
                                >
                                  <Edit className="h-3 w-3 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteClick(item)}
                                  className="flex-1 sm:flex-none h-9 text-xs sm:text-sm text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-40 p-8 text-center">
                    <div className="text-4xl opacity-50 mb-3">📋</div>
                    <div className="text-lg font-medium mb-1">
                      {hasActiveFilters ? 'No results found' : 'No records yet'}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {hasActiveFilters
                        ? 'Try adjusting your search/filters, or clear them to see everything.'
                        : 'Create your first record to get started.'}
                    </div>
                    <div className="pt-3 flex items-center justify-center gap-2">
                      {hasActiveFilters ? (
                        <Button variant="outline" size="sm" onClick={clearFilters}>
                          Clear filters
                        </Button>
                      ) : (
                        <Button size="sm" onClick={onAdd}>
                          <Plus className="mr-2 h-4 w-4" />
                          Add New
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
        
        {/* Pagination Controls - Only show for server-side mode */}
        {serverSide && pagination && (
          <div className="px-6 py-4 border-t">
            <div className="flex flex-col gap-4">
              <div className="text-sm text-muted-foreground text-center sm:text-left">
                Showing <span className="font-medium text-foreground">{((pagination.page - 1) * pagination.limit) + 1}</span> to{' '}
                <span className="font-medium text-foreground">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of{' '}
                <span className="font-medium text-foreground">{pagination.total}</span> results
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Rows per page:</span>
                    <Select
                      value={pagination.limit.toString()}
                      onValueChange={(value) => {
                        onPageSizeChange?.(parseInt(value));
                        resetToFirstPage();
                      }}
                    >
                      <SelectTrigger className="w-[80px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5</SelectItem>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="200">200</SelectItem>
                        <SelectItem value="500">500</SelectItem>
                        <SelectItem value="1000">1000</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex sm:hidden items-center justify-between gap-2 w-full">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onPageChange?.(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                    >
                      Prev
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page <span className="font-medium text-foreground">{pagination.page}</span> of{' '}
                      <span className="font-medium text-foreground">{pagination.totalPages}</span>
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onPageChange?.(pagination.page + 1)}
                      disabled={pagination.page >= pagination.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>

                <div className="hidden sm:flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange?.(1)}
                    disabled={pagination.page <= 1}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange?.(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                  >
                    Previous
                  </Button>
                  <span className="px-3 py-1 text-sm">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange?.(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange?.(pagination.totalPages)}
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    Last
                  </Button>
                  <div className="flex items-center gap-2 border-l border-border pl-3">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">Go to page</span>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={pagination.totalPages}
                      value={pageJump}
                      onChange={(event) => setPageJump(event.target.value)}
                      onBlur={() => commitPageJump(pageJump)}
                      onKeyDown={handlePageJumpKeyDown}
                      className="w-20 h-9 text-sm"
                      aria-label="Go to page"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => commitPageJump(pageJump)}
                      disabled={
                        !pageJump ||
                        Number.isNaN(Number(pageJump)) ||
                        Number(pageJump) < 1 ||
                        Number(pageJump) > pagination.totalPages ||
                        Number(pageJump) === pagination.page
                      }
                    >
                      Go
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      <Dialog
        open={!!deleteDialog}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteDialog(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{deleteDialogTitle || 'Confirm delete'}</DialogTitle>
            {deleteDialogDescription && (
              <DialogDescription>{deleteDialogDescription}</DialogDescription>
            )}
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>
              {deleteDialogCancelLabel}
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              {deleteDialogConfirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
